import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import { api, formatBRL, formatDateBR } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Layers, Loader2, Plus, ArrowRight, AlertCircle, CheckCircle2, Clock, FileText, Handshake, Search, X, LifeBuoy } from "lucide-react";
import { toast } from "sonner";
import NewInstallmentContractModal from "../components/NewInstallmentContractModal";
import InstallmentsDashboard from "../components/InstallmentsDashboard";
import BatchRecoveryModal from "../components/BatchRecoveryModal";

const VIEWS = [
  { id: "list", label: "Lista" },
  { id: "dashboard", label: "Dashboard" },
];

const TABS = [
  { id: "all", label: "Todos" },
  { id: "parcelado", label: "Parcelados" },
  { id: "acordo", label: "Acordos" },
  { id: "ok", label: "Em dia" },
  { id: "late", label: "Atrasados" },
  { id: "quitado", label: "Quitados" },
];

function statusBadge(ic) {
  if (ic.status === "quitado") return { label: "Quitado", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40", Icon: CheckCircle2 };
  if (ic.status === "cancelado") return { label: "Cancelado", cls: "bg-zinc-500/15 text-zinc-300 border-zinc-500/40", Icon: AlertCircle };
  if ((ic.overdue_count || 0) > 0) return { label: "Atrasado", cls: "bg-red-500/15 text-red-300 border-red-500/40", Icon: AlertCircle };
  return { label: "Em dia", cls: "bg-amber-500/15 text-amber-300 border-amber-500/40", Icon: Clock };
}

export default function ParceladosPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const [openNew, setOpenNew] = useState(false);
  const [view, setView] = useState("list");
  const [search, setSearch] = useState("");
  const [fIndicator, setFIndicator] = useState("");
  const [fSocio, setFSocio] = useState("");
  const [indicators, setIndicators] = useState([]);
  const [socios, setSocios] = useState([]);
  // iter71 — Seleção em lote
  const canEdit = user?.role === "admin" || user?.role === "gestora";
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [batchOpen, setBatchOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (fIndicator) params.indicator_id = fIndicator;
      if (fSocio) params.socio_id = fSocio;
      const { data } = await api.get("/installment-contracts", { params });
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error("Falha ao carregar parcelados");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role !== "socio" && user?.role !== "indicador") {
      api.get("/indicators").then(r => setIndicators(r.data)).catch(() => {});
      if (user?.role === "admin") {
        api.get("/socios").then(r => setSocios(r.data)).catch(() => {});
      }
    }
  }, [user]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [search, fIndicator, fSocio]);

  const clearFilters = () => {
    setSearch("");
    setFIndicator("");
    setFSocio("");
  };
  const hasFilters = !!(search || fIndicator || fSocio);

  const filtered = useMemo(() => {
    return items.filter((ic) => {
      if (tab === "all") return true;
      if (tab === "parcelado") return ic.kind === "parcelado";
      if (tab === "acordo") return ic.kind === "acordo";
      if (tab === "quitado") return ic.status === "quitado";
      if (tab === "late") return (ic.overdue_count || 0) > 0 && ic.status === "ativo";
      if (tab === "ok") return (ic.overdue_count || 0) === 0 && ic.status === "ativo";
      return true;
    });
  }, [items, tab]);

  const counts = useMemo(() => ({
    all: items.length,
    parcelado: items.filter((i) => i.kind === "parcelado").length,
    acordo: items.filter((i) => i.kind === "acordo").length,
    quitado: items.filter((i) => i.status === "quitado").length,
    late: items.filter((i) => (i.overdue_count || 0) > 0 && i.status === "ativo").length,
    ok: items.filter((i) => (i.overdue_count || 0) === 0 && i.status === "ativo").length,
  }), [items]);

  // iter71 — IDs elegíveis (ativos + não em recovery)
  const selectableIds = useMemo(() =>
    new Set(filtered.filter(ic => ic.status === "ativo" && !ic.in_recovery).map(ic => ic.id)),
    [filtered]
  );
  const toggleOne = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };
  const toggleAll = () => {
    const allSel = selectableIds.size > 0 && [...selectableIds].every(id => selectedIds.has(id));
    const next = new Set(selectedIds);
    if (allSel) selectableIds.forEach(id => next.delete(id));
    else selectableIds.forEach(id => next.add(id));
    setSelectedIds(next);
  };
  useEffect(() => { setSelectedIds(new Set()); }, [tab, search, fIndicator, fSocio]);

  return (
    <Layout>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] flex items-center gap-1.5">
            <Layers size={11} /> Operações alternativas
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold mt-1">Parcelados e Acordos</h1>
          <p className="text-[13px] text-[#A3A3A3] mt-2 max-w-2xl">
            Contratos com parcelas fixas (capital + juros divididos) e acordos de renegociação.
            Visualmente separados dos contratos normais de 30 dias.
          </p>
        </div>
        <button
          onClick={() => setOpenNew(true)}
          className="fc-btn-gold flex items-center gap-2"
          data-testid="new-installment-contract-btn"
        >
          <Plus size={15} /> Novo parcelado / acordo
        </button>
      </div>

      {/* Toggle Lista / Dashboard */}
      <div className="flex gap-1.5 mb-5 border-b border-[#1f1f1f]" data-testid="view-toggle">
        {VIEWS.map((v) => (
          <button
            key={v.id}
            onClick={() => setView(v.id)}
            data-testid={`view-${v.id}`}
            className={`px-4 py-2 text-[13px] border-b-2 transition-colors -mb-px ${
              view === v.id
                ? "border-[#D4AF37] text-[#D4AF37]"
                : "border-transparent text-[#A3A3A3] hover:text-white"
            }`}
          >
            {v.label}
          </button>
        ))}
      </div>

      {view === "dashboard" ? (
        <InstallmentsDashboard />
      ) : (
      <>
      {/* Barra de busca e filtros — paridade com ContratosPage (iter66) */}
      <div className="fc-card p-4 mb-4 flex flex-wrap gap-3 items-center" data-testid="installments-filter-bar">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#737373]" />
          <input
            className="fc-input pl-9"
            placeholder="Buscar nome, CPF..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            data-testid="installments-search-input"
          />
        </div>
        {user?.role !== "socio" && user?.role !== "indicador" && (
          <>
            <select
              className="fc-input min-w-[160px]"
              value={fIndicator}
              onChange={e => setFIndicator(e.target.value)}
              data-testid="installments-filter-indicator"
            >
              <option value="">Indicadores</option>
              {indicators.map(i => (<option key={i.id} value={i.id}>{i.name}</option>))}
            </select>
            {user?.role === "admin" && (
              <select
                className="fc-input min-w-[160px]"
                value={fSocio}
                onChange={e => setFSocio(e.target.value)}
                data-testid="installments-filter-socio"
              >
                <option value="">Sócios</option>
                {socios.map(s => (<option key={s.id} value={s.id}>{s.name}</option>))}
              </select>
            )}
          </>
        )}
        {hasFilters && (
          <button
            onClick={clearFilters}
            className="text-[12px] text-[#A3A3A3] hover:text-white flex items-center gap-1 px-2 py-1"
            data-testid="installments-clear-filters"
          >
            <X size={13} /> Limpar Filtros
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-1.5 mb-5" data-testid="installment-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`tab-${t.id}`}
            className={`px-3 py-1.5 rounded-md text-[12.5px] border transition-colors ${
              tab === t.id
                ? "bg-[#D4AF37]/10 border-[#D4AF37]/40 text-[#D4AF37]"
                : "bg-[#0a0a0a] border-[#1f1f1f] text-[#A3A3A3] hover:text-white hover:border-[#262626]"
            }`}
          >
            {t.label} <span className="ml-1 text-[10px] text-[#737373]">({counts[t.id] || 0})</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-[#737373] text-sm">
          <Loader2 size={14} className="animate-spin" /> Carregando...
        </div>
      ) : filtered.length === 0 ? (
        <div className="fc-card p-10 text-center" data-testid="installment-empty">
          <Layers size={28} className="text-[#737373] mx-auto mb-3" />
          <div className="text-sm text-[#A3A3A3]">
            {items.length === 0 ? "Nenhum parcelado ou acordo cadastrado." : "Nenhum item nesta categoria."}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="installment-list">
          {canEdit && selectableIds.size > 0 && (
            <div className="col-span-full flex items-center gap-2 text-[12px] text-[#A3A3A3]">
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={[...selectableIds].every(id => selectedIds.has(id))}
                  onChange={toggleAll}
                  className="accent-[#D4AF37]"
                  data-testid="installments-select-all"
                />
                Selecionar todos os {selectableIds.size} elegíveis desta aba
              </label>
            </div>
          )}
          {filtered.map((ic) => {
            const sb = statusBadge(ic);
            const Icon = ic.kind === "parcelado" ? FileText : Handshake;
            const KindCls = ic.kind === "parcelado" ? "text-blue-300 border-blue-500/30 bg-blue-500/10" : "text-violet-300 border-violet-500/30 bg-violet-500/10";
            const selectable = canEdit && selectableIds.has(ic.id);
            return (
              <div key={ic.id} className="relative">
                {selectable && (
                  <div className="absolute top-2 left-2 z-10">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(ic.id)}
                      onChange={(e) => { e.stopPropagation(); toggleOne(ic.id); }}
                      onClick={(e) => e.stopPropagation()}
                      className="accent-[#D4AF37] cursor-pointer w-4 h-4"
                      data-testid={`installment-select-${ic.id}`}
                    />
                  </div>
                )}
                <Link
                  to={`/parcelados/${ic.id}`}
                  className={`fc-card p-4 hover:border-[#D4AF37]/40 transition-colors block ${selectable ? "pl-9" : ""}`}
                  data-testid={`installment-row-${ic.id}`}
                >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 mb-1">
                      <span className={`badge-base border ${KindCls} text-[10px]`}>
                        <Icon size={9} /> {ic.kind === "parcelado" ? "PARCELADO" : "ACORDO"}
                      </span>
                      <span className={`badge-base border ${sb.cls} text-[10px]`}>
                        <sb.Icon size={9} /> {sb.label}
                      </span>
                    </div>
                    <div className="text-[15px] font-semibold truncate">{ic.client_name}</div>
                    <div className="text-[11.5px] text-[#737373] mt-0.5">Sócio: {ic.socio_name || "—"}</div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-3 text-[12.5px]">
                  <div>
                    <div className="text-[10px] text-[#737373] uppercase tracking-wider">Total</div>
                    <div className="font-mono text-white">{formatBRL(ic.total_amount)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-[#737373] uppercase tracking-wider">Saldo</div>
                    <div className="font-mono text-[#D4AF37]">{formatBRL(ic.balance_remaining)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-[#737373] uppercase tracking-wider">Parcelas</div>
                    <div className="text-white">
                      {ic.paid_count || 0}/{ic.installments_count}
                      <span className="text-[#737373] ml-1 text-[11px]">({ic.frequency === "weekly" ? "sem" : "mês"})</span>
                    </div>
                    {/* Barra de progresso visual */}
                    <div className="mt-1.5 h-1.5 rounded-full bg-[#1f1f1f] overflow-hidden" data-testid={`progress-${ic.id}`}>
                      <div
                        className="h-full bg-gradient-to-r from-[#D4AF37] to-emerald-400 transition-all"
                        style={{ width: `${Math.min(100, Math.round(((ic.paid_count || 0) / Math.max(ic.installments_count || 1, 1)) * 100))}%` }}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] text-[#737373] uppercase tracking-wider">Próx. vencimento</div>
                    <div className="text-white">{ic.next_due_date ? formatDateBR(ic.next_due_date) : "—"}</div>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-[#1f1f1f] flex items-center justify-end">
                  <span className="text-[11px] text-[#D4AF37] flex items-center gap-1">
                    Ver detalhes <ArrowRight size={11} />
                  </span>
                </div>
              </Link>
              </div>
            );
          })}
        </div>
      )}

      {openNew && (
        <NewInstallmentContractModal
          onClose={() => setOpenNew(false)}
          onCreated={() => { setOpenNew(false); load(); }}
        />
      )}
      </>
      )}

      {/* iter71 — Barra flutuante + modal batch */}
      {canEdit && selectedIds.size > 0 && !batchOpen && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 fc-card border-[#D4AF37]/40 shadow-2xl px-5 py-3 flex items-center gap-4" data-testid="installments-batch-bar">
          <div className="text-[12.5px]">
            <span className="text-[#D4AF37] font-semibold" data-testid="installments-batch-count">
              {selectedIds.size}
            </span> operação(ões) selecionada(s)
          </div>
          <button
            onClick={() => setSelectedIds(new Set())}
            className="text-[11.5px] text-[#A3A3A3] hover:text-white underline underline-offset-2"
          >
            Limpar
          </button>
          <button
            onClick={() => setBatchOpen(true)}
            className="fc-btn-gold text-xs flex items-center gap-1.5"
            data-testid="installments-batch-send-recovery"
          >
            <LifeBuoy size={13}/> Enviar para Recuperação
          </button>
        </div>
      )}
      {batchOpen && (
        <BatchRecoveryModal
          items={[...selectedIds].map(id => ({ record_type: "installment_contract", record_id: id }))}
          onClose={() => setBatchOpen(false)}
          onDone={() => { setBatchOpen(false); setSelectedIds(new Set()); load(); }}
        />
      )}
    </Layout>
  );
}
